/** @ken/module-estimates — frontend entry point.
 *
 * Mirrors @ken/module-invoicing shape. Estimates are pre-sale quotes
 * with line items + Job-or-Customer parentage. Source-canonical fields
 * + custom_field_values render via the unified tab system (canonical
 * layout + field definitions, merged client-side per ADR-071).
 *
 * 0.2.0 — story-page adoption (ADR-0081). Adds estimates/$id full-page
 * detail with Overview / Line Items / Related / Notes tabs.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { EstimateStorySearch } from "./routes/story/estimate-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadEstimateStoryTabs = () => import("./routes/story/estimate-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const EstimateCreatePage = lazyScreen(
  () => import("./routes/estimate-create"),
  "EstimateCreatePage",
);
const EstimateEditPage = lazyScreen(
  () => import("./routes/estimate-edit"),
  "EstimateEditPage",
);
const EstimateListPage = lazyScreen(
  () => import("./routes/estimate-list"),
  "EstimateListPage",
);
const EstimateStoryDetail = lazyScreen(
  () => import("./routes/story/estimate-story-detail"),
  "EstimateStoryDetail",
);
const EstimateActivityRoute = lazyScreen(
  loadEstimateStoryTabs,
  "EstimateActivityRoute",
);
const EstimateLineItemsRoute = lazyScreen(
  loadEstimateStoryTabs,
  "EstimateLineItemsRoute",
);
const EstimateNotesRoute = lazyScreen(
  loadEstimateStoryTabs,
  "EstimateNotesRoute",
);
const EstimateOverviewRoute = lazyScreen(
  loadEstimateStoryTabs,
  "EstimateOverviewRoute",
);
const EstimateRelatedRoute = lazyScreen(
  loadEstimateStoryTabs,
  "EstimateRelatedRoute",
);
export { EstimateDetailSheet } from "./components/estimate-detail-sheet";

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "estimates",
    validateSearch: (raw: Record<string, unknown>): EstimateListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: EstimateListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "estimates/new",
    component: EstimateCreatePage,
  });

  // Edit (backlog B2): a focused FULL-PAGE form, deep-linkable, NOT nested
  // under the story shell. Parented directly to the module root so it renders
  // standalone (no StoryPageDetail header/tabs around it). Registered BEFORE
  // the `estimates/$id` parent so the `/edit` literal segment is matched.
  const editRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "estimates/$id/edit",
    component: EstimateEditPage,
  });

  // ── Story-page detail (ADR-0081): parent estimates/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "estimates/$id",
    validateSearch: (raw: Record<string, unknown>): EstimateStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: EstimateStoryDetail,
  });

  // Index redirect estimates/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/estimates/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: EstimateOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: EstimateLineItemsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: EstimateRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: EstimateNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: EstimateActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [listRoute, createRouteEntry, editRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface EstimateListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
